import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useActivePage, useNotifications } from '@toolpad/core';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router';
import { Card, CardContent, Stack } from '@mui/material';

import { QueryRenderer } from '@barboxx/admin-components';
import { useAuth } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { User } from '@barboxx/models';

import { RootContainer } from '../../components/nav/RootContainer';
import { USER_SCHEMA, UserForm } from '../components/UserForm';
import { UserPasswordCard } from './components/UserPasswordCard';

interface UserDetailsCardProps {
    user: User;
    onSuccess: () => void;
}

const UserDetailsCard = ({ user, onSuccess }: UserDetailsCardProps) => {
    const notifications = useNotifications();

    const formContext = useForm<User.UpdateRequest>({
        resolver: yupResolver(USER_SCHEMA),
        defaultValues: user,
    });

    const mutateUser = useMutation({
        mutationKey: ['updateUser', user.id],
        mutationFn: (data: User.UpdateRequest) =>
            Api.Users.updateUser(Number(user.id), data),
    });

    const onSubmit = async (values: User.UpdateRequest) => {
        return mutateUser
            .mutateAsync(values)
            .then(async ({ data }) => {
                notifications.show('Updated user!', {
                    severity: 'success',
                });
                onSuccess();
            })
            .catch(e => {
                notifications.show('Error Updating user!', {
                    severity: 'error',
                });
            });
    };

    return (
        <Card>
            <CardContent>
                <UserForm formContext={formContext} onSubmit={onSubmit} />
            </CardContent>
        </Card>
    );
};

export function UserDetails() {
    const params = useParams();
    const activePage = useActivePage();
    const {
        auth: { user },
    } = useAuth();

    const id = Number(params['id']);
    const isCurrentUser = user?.id === id;

    const userQueryResults = useQuery({
        queryKey: ['user', id],
        queryFn: () => Api.Users.getUser(id),
    });

    const breadcrumbs = [
        ...(activePage?.breadcrumbs ?? []),
        { title: 'Details', path: `${activePage?.path}/${id}` },
    ];

    return (
        <RootContainer breadcrumbs={breadcrumbs} title="User Details">
            <QueryRenderer results={userQueryResults}>
                {(data, actions) => {
                    return (
                        <Stack gap={2} sx={{ paddingBottom: 4 }}>
                            <UserDetailsCard
                                user={data}
                                onSuccess={actions.refetch}
                            />
                            {isCurrentUser && <UserPasswordCard user={data} />}
                        </Stack>
                    );
                }}
            </QueryRenderer>
        </RootContainer>
    );
}
